import type { ReactNode } from 'react'
import { use, useEffect } from 'react'
import { SettingsSaveBarSlotContext } from '@/components/shared/settings/settings-embed'
import { Button } from '@/components/ui/button'
import { DisclosureSummary } from '@/components/ui/disclosure-summary'
import { cn } from '@/lib/utils'
import { CircleNotchIcon, WarningCircleIcon } from '@phosphor-icons/react'
import { createPortal } from 'react-dom'

interface StickySaveBarProps {
  visible: boolean
  message?: ReactNode
  onSave: () => void
  onDiscard?: () => void
  saveLabel?: string
  discardLabel?: string
  isSaving?: boolean
  canSave?: boolean
  extraActions?: ReactNode
  /**
   * Error banner shown inside the bar when a save fails (FR-X5). A short headline
   * string or node; `errorDetail` holds the optional expandable per-item detail.
   */
  error?: ReactNode
  /** Expandable detail rendered under the error headline (e.g. per-item failures). */
  errorDetail?: ReactNode
  className?: string
}

/**
 * Ghost inks on the bar's dark surface (the mass-actions bar grammar). The
 * dark: duplicates beat the ghost variant's own dark: classes.
 */
const BAR_GHOST_CLASSES = cn(
  'text-white/70 hover:text-white dark:hover:text-white',
  'hover:bg-white/[0.08] active:bg-white/[0.12] dark:hover:bg-white/[0.08]',
  'focus-visible:ring-white/30 focus-visible:ring-offset-0',
)

/** The primary action inverted onto the dark surface: a white pill. */
const BAR_SAVE_CLASSES = cn(
  'bg-white text-[hsl(var(--floating-bar))] hover:bg-white/90 dark:bg-white dark:text-[hsl(var(--floating-bar))] dark:hover:bg-white/90',
  'focus-visible:ring-white/40 focus-visible:ring-offset-0',
  'disabled:bg-white/40 disabled:text-[hsl(var(--floating-bar)/0.7)]',
)

/**
 * The one save bar for buffered edits: a dark floating bar, the same fixed
 * ink surface as the mass-actions bar, that slides in while a form has
 * unsaved changes. A yolk dot and "Unsaved changes" mark it, Discard and
 * Save sit at its end, and ⌘S / Ctrl+S saves while it shows.
 *
 * Inside the Settings shell it floats at the bottom of the settings pane
 * (the shell's save-bar slot); everywhere else it is fixed to the bottom of
 * the viewport. Pair it with UnsavedChangesGuard, or use UnsavedChangesBar,
 * which is both.
 */
export function StickySaveBar({
  visible,
  message,
  onSave,
  onDiscard,
  saveLabel = 'Save changes',
  discardLabel = 'Discard',
  isSaving = false,
  canSave = true,
  extraActions,
  error,
  errorDetail,
  className,
}: StickySaveBarProps) {
  const slot = use(SettingsSaveBarSlotContext)
  const isSaveDisabled = isSaving || !canSave

  // ⌘S / Ctrl+S saves while the bar is up (and keeps the browser's own
  // "Save page" dialog out of the way).
  useEffect(() => {
    if (!visible) return
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.key.toLowerCase() !== 's' ||
        !(event.metaKey || event.ctrlKey) ||
        event.shiftKey ||
        event.altKey
      ) {
        return
      }
      event.preventDefault()
      if (!isSaveDisabled) onSave()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [visible, isSaveDisabled, onSave])

  const bar = (
    <div
      role="region"
      aria-label="Unsaved changes"
      className={cn(
        'pointer-events-auto flex w-full max-w-xl flex-col gap-1.5 rounded-2xl border border-black/50 bg-[hsl(var(--floating-bar))] p-1.5 pl-4 text-white shadow-[var(--shadow-floating)]',
        error &&
          'shadow-[var(--shadow-floating),0_0_0_1px_rgb(248_113_113/0.5)]',
      )}
    >
      {error && (
        <div
          role="alert"
          className="flex flex-col gap-1 pr-2.5 pt-1.5 text-xs text-red-300"
        >
          <div className="flex items-center gap-1.5 font-medium">
            <WarningCircleIcon className="h-3.5 w-3.5 shrink-0" weight="fill" />
            {error}
          </div>
          {errorDetail && (
            <details className="group/disclosure ml-5 text-[11px] text-red-300/90">
              <DisclosureSummary className="inline-flex">
                Details
              </DisclosureSummary>
              <div className="mt-1 space-y-0.5">{errorDetail}</div>
            </details>
          )}
        </div>
      )}
      <div className="flex items-center justify-between gap-3">
        <div
          className="flex min-w-0 items-center gap-2 text-sm font-medium text-white/90"
          aria-live="polite"
        >
          <span
            className="h-2 w-2 shrink-0 rounded-full bg-[hsl(var(--sunny-yolk))] shadow-[0_0_0_3px_hsl(var(--sunny-yolk)/0.25)]"
            aria-hidden="true"
          />
          <span className="truncate">{message ?? 'Unsaved changes'}</span>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {extraActions}
          {onDiscard && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onDiscard}
              disabled={isSaving}
              className={BAR_GHOST_CLASSES}
            >
              {discardLabel}
            </Button>
          )}
          <Button
            type="button"
            size="sm"
            onClick={onSave}
            disabled={isSaveDisabled}
            className={cn('min-w-[7.5rem]', BAR_SAVE_CLASSES)}
            title="Save (⌘S)"
          >
            {isSaving ? (
              <>
                <CircleNotchIcon className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                Saving…
              </>
            ) : error ? (
              'Retry save'
            ) : (
              saveLabel
            )}
          </Button>
        </div>
      </div>
    </div>
  )

  if (slot) {
    if (!visible) return null
    return createPortal(
      <div
        className={cn(
          'flex justify-center pt-4 duration-200 animate-in fade-in-0 slide-in-from-bottom-2',
          className,
        )}
      >
        {bar}
      </div>,
      slot,
    )
  }

  return (
    <div
      aria-hidden={!visible}
      inert={!visible}
      className={cn(
        'transition-tonal pointer-events-none fixed inset-x-0 bottom-0 z-50 flex justify-center px-4 pb-6 duration-200 max-md:pb-[calc(1rem+env(safe-area-inset-bottom))]',
        visible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0',
        className,
      )}
    >
      {bar}
    </div>
  )
}
